// Product zoom lightbox — native <dialog>, zero-dep, deferred. Contract: every
// element under [data-product-root] carrying data-lightbox-src (+ optional
// -srcset / -thumb / -alt) is an item, in DOM order — gallery image slides, video slides
// and review-photos tiles all participate. An item holding a <template> is a video: the
// stage shows its poster and hydrates the template on play, so a clip is watched
// full-screen instead of inside a stage slide sized for box shots. Esc + focus trap are
// native dialog semantics; arrows, prev/next and the thumb strip are wired here.
(() => {
  const root = document.querySelector('[data-product-root]');
  const dlg = root && root.querySelector('[data-product-lightbox]');
  if (!dlg || typeof dlg.showModal !== 'function') return;

  const items = [...root.querySelectorAll('[data-lightbox-src]')];
  if (!items.length) return;

  const stage = dlg.querySelector('[data-lightbox-stage]');
  const strip = dlg.querySelector('[data-lightbox-strip]');

  // One child per item in a scroll-snap rail, rather than one image swapped in place:
  // panning between shots is then the browser's own touch gesture (and wheel, and
  // trackpad), which is what a phone visitor reaches for first. `loading: lazy` means a
  // ten-shot product still only fetches what is on screen.
  //
  // box -> { tpl, poster, btn }. A video item is a poster plus a play button until it is
  // asked for, then the slide's own <template> (the same one product.js used to swap in
  // place) replaces them in the same box. Kept in the map after playing so the poster can
  // be put back — that is what stops a hosted <video> or a provider iframe carrying on
  // with sound behind a closed dialog, which pausing alone cannot do for an iframe.
  const players = new Map();

  const poster = (el) => {
    const im = document.createElement('img');
    im.className = 'product-lightbox__img';
    im.sizes = '100vw';
    im.loading = 'lazy';
    im.decoding = 'async';
    im.srcset = el.dataset.lightboxSrcset || '';
    im.src = el.dataset.lightboxSrc;
    im.alt = el.dataset.lightboxAlt || '';
    return im;
  };

  items.forEach((el) => {
    const tpl = el.querySelector('template');
    if (!tpl) return stage.append(poster(el));
    const box = document.createElement('div');
    box.className = 'product-lightbox__player';
    // The slide already carries the media's own ratio; the player box needs it too, or a
    // provider iframe is handed a stage-shaped box and letterboxes itself inside it.
    const ratio = el.style.getPropertyValue('--media-aspect');
    if (ratio) box.style.setProperty('--media-aspect', ratio);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-lightbox__play';
    btn.setAttribute('aria-label', dlg.dataset.playLabel || 'Play');
    btn.innerHTML = '<svg viewBox="0 0 10 12" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M0 0l10 6-10 6V0z"/></svg>';
    btn.addEventListener('click', () => play(box));
    box.append(poster(el), btn);
    players.set(box, { tpl, poster: box.firstElementChild, btn });
    stage.append(box);
  });

  const play = (box) => {
    const p = players.get(box);
    // Already hydrated — the poster is only the first child while it is standing by.
    if (!p || box.firstElementChild !== p.poster) return;
    box.replaceChildren(p.tpl.content.cloneNode(true));
    // play() here rather than an autoplay attribute: this runs inside the click that
    // opened the viewer, so user activation lets it start with sound. (external_video is
    // an iframe, so the ?. no-ops and its URL's own autoplay flag runs.)
    box.querySelector('video')?.play().catch(() => {});
  };

  // Anything not `keep` goes back to its poster: swiping to the next shot, or closing.
  const stopPlayers = (keep) => {
    players.forEach((p, box) => {
      if (box !== keep && box.firstElementChild !== p.poster) box.replaceChildren(p.poster, p.btn);
    });
  };

  let idx = 0;
  const mark = (i) => {
    idx = i;
    thumbs.forEach((t, j) => t.classList.toggle('is-active', j === idx));
    stopPlayers(stage.children[idx]);
  };
  const show = (i, behavior) => {
    const next = (i + items.length) % items.length;
    mark(next);
    // Slides are exactly one stage wide, so the offset IS the index. scrollTo rather than
    // scrollIntoView: the latter also scrolls the ancestors, and the dialog is one of them.
    stage.scrollTo({ left: next * stage.clientWidth, behavior: behavior || 'auto' });
  };

  // The scroll position is the source of truth, so a swipe, a wheel, an arrow and a
  // thumbnail all converge on one code path.
  let settle = 0;
  stage.addEventListener(
    'scroll',
    () => {
      clearTimeout(settle);
      settle = setTimeout(() => mark(Math.round(stage.scrollLeft / stage.clientWidth)), 100);
    },
    { passive: true }
  );

  const thumbs = items.map((el, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'product-lightbox__thumb';
    b.setAttribute('aria-label', (dlg.dataset.thumbLabel || 'Image') + ' ' + (i + 1) + ' / ' + items.length);
    const t = document.createElement('img');
    t.src = el.dataset.lightboxThumb || el.dataset.lightboxSrc;
    t.alt = '';
    t.loading = 'lazy';
    b.append(t);
    b.addEventListener('click', () => show(i, 'smooth'));
    strip.append(b);
    return b;
  });

  const open = (i) => {
    dlg.showModal(); // first: a stage that is still display:none measures clientWidth 0
    show(i);
    play(stage.children[i]); // no-op unless the item opened is a video
  };

  items.forEach((el, i) => el.addEventListener('click', () => open(i)));

  // The zoom pill is one element on the carousel, not one per slide (it used to scroll away
  // with its image). It has no item of its own, so it asks the track which slide is on
  // screen — slides are one viewport wide and scroll-snapped, so the scroll offset IS the
  // index — and hides on a slide that isn't a lightbox item (3D, which keeps its own
  // in-slide load button; a video slide IS an item now and opens to play).
  const track = root.querySelector('[data-product-stage]');
  const pill = root.querySelector('[data-zoom-open]');
  if (track && pill) {
    const current = () => items.indexOf(track.children[Math.round(track.scrollLeft / track.clientWidth)]);
    pill.addEventListener('click', () => {
      const i = current();
      if (i < 0) return;
      open(i);
    });
    const sync = () => { pill.hidden = current() < 0; };
    track.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  // Teardown is explicit, not left to the dialog's `close` event: that event is the only
  // signal an Esc dismissal gives, so it is still listened for below, but it cannot be the
  // ONLY path — Chrome does not reliably fire it, and a viewer that closes without putting
  // the poster back leaves a provider iframe playing with sound behind the closed dialog.
  const dismiss = () => {
    dlg.close();
    stopPlayers();
  };
  dlg.querySelector('[data-lightbox-close]').addEventListener('click', dismiss);
  dlg.addEventListener('close', () => stopPlayers());
  dlg.addEventListener('cancel', () => setTimeout(stopPlayers, 0));
  dlg.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(idx - 1, 'smooth'));
  dlg.querySelector('[data-lightbox-next]').addEventListener('click', () => show(idx + 1, 'smooth'));
  dlg.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') show(idx - 1, 'smooth');
    else if (e.key === 'ArrowRight') show(idx + 1, 'smooth');
  });
  // Backdrop (scrim) click closes — the dialog itself is the full-viewport scrim,
  // so any click that isn't on the image/controls counts.
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg || e.target === stage) dismiss();
  });
})();

// Desktop hover magnifier. One delegated pointermove feeds --pg-zx/--pg-zy (cursor
// as %) to the GALLERY — a common ancestor of both the stage and the side pane
// (which sits outside the overflow-clipped stage), so both inherit it. CSS scales
// the hovered image in place, or clones it into the pane + lens (pane style).
// --pg-scale stays per-slide (only the hovered one scales). Bound on the stage so
// it survives product.js variant re-renders. Image slides only (data-hover-zoom).
(() => {
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const root = document.querySelector('[data-product-root]');
  const stage = root && root.querySelector('[data-product-stage]');
  if (!stage) return;
  // The gallery, not stage.parentElement — the stage sits inside <scroll-carousel> now, and
  // the zoom pane is outside it. The --pg-* vars have to land on their common ancestor.
  const gallery = stage.closest('.product-main__gallery') || stage.parentElement;
  const pane = gallery.querySelector('[data-zoompane]');
  const paneImg = pane && pane.querySelector('img');
  let current = null;
  // Read once, not per pointermove — getComputedStyle forces a style recalc.
  let zoom = 0;
  stage.addEventListener('pointermove', (e) => {
    const slide = e.target.closest('.product-main__slide[data-hover-zoom]');
    if (!slide) return;
    const r = slide.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    // Raw cursor position drives IN-PLACE zoom, where scaling about the cursor is
    // what you want and never reveals past an edge.
    gallery.style.setProperty('--pg-zx', px * 100 + '%');
    gallery.style.setProperty('--pg-zy', py * 100 + '%');
    // Pane mode needs a second, different mapping. The lens is a rect of size 1/S
    // centred on the cursor and clamped to the slide, so its start is
    // clamp(0, p - 1/2S, 1 - 1/S). The pane is a scale(S) whose transform-origin q
    // puts the visible region's start at q*(S-1)/S. Those two agree ONLY at the
    // centre and the extremes: at p=0.25,S=2 the lens starts at 0 but the pane at
    // 0.125, and inside the clamped zone the lens freezes while the pane keeps
    // panning. Derive both from ONE clamped lens position so they always match.
    if (!zoom) zoom = parseFloat(getComputedStyle(gallery).getPropertyValue('--pg-zoom')) || 2;
    if (zoom > 1) {
      const half = 0.5 / zoom;
      const lx = Math.min(Math.max(px, half), 1 - half) - half; // lens start, 0..1-1/S
      const ly = Math.min(Math.max(py, half), 1 - half) - half;
      const k = zoom / (zoom - 1); // q such that q*(S-1)/S === lens start
      gallery.style.setProperty('--pg-lx', lx * 100 + '%');
      gallery.style.setProperty('--pg-ly', ly * 100 + '%');
      gallery.style.setProperty('--pg-ox', lx * k * 100 + '%');
      gallery.style.setProperty('--pg-oy', ly * k * 100 + '%');
    }
    slide.style.setProperty('--pg-scale', 'var(--pg-zoom, 2)');
    if (paneImg && current !== slide) {
      // Hi-res pane source: lightbox candidates when present, else the slide's own
      // image. Set here (not markup) so it loads on first hover only.
      current = slide;
      // Pane box must be geometrically similar to the slide box, or the shared
      // percentage mapping points elsewhere. Only the width is CSS-matched; the
      // ratio is measured because "adapt to image" gives each slide its own.
      gallery.style.setProperty('--pane-aspect', r.width / r.height);
      const im = slide.querySelector('img');
      paneImg.srcset = slide.dataset.lightboxSrcset || (im && im.srcset) || '';
      paneImg.src = slide.dataset.lightboxSrc || (im && (im.currentSrc || im.src)) || '';
    }
    if (pane) pane.classList.add('is-visible');
  });
  // Zoom off when the pointer truly leaves the slide (not just crosses children).
  stage.addEventListener('pointerout', (e) => {
    const slide = e.target.closest('.product-main__slide[data-hover-zoom]');
    if (!slide || (e.relatedTarget && slide.contains(e.relatedTarget))) return;
    slide.style.removeProperty('--pg-scale');
    if (pane) { pane.classList.remove('is-visible'); current = null; }
  });
})();
